import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { api, errorMessage } from "@/lib/api";
import { domainLabel, todayISO } from "@/lib/aba";
import { ACTIONS } from "@/constants/testIds";

export const ActionDialog = ({ open, onOpenChange, patientId, domain, action, onSaved }) => {
  const qc = useQueryClient();
  const isEdit = Boolean(action);
  const [form, setForm] = useState({ nome: "", descrizione: "", criterio: 80, stato: "introdotto", data_introduzione: todayISO(), data_acquisizione: null, data_obiettivo: null });

  useEffect(() => {
    if (!open) return;
    setForm(
      action
        ? { nome: action.nome, descrizione: action.descrizione || "", criterio: action.criterio || 80, stato: action.stato, data_introduzione: action.data_introduzione || todayISO(), data_acquisizione: action.data_acquisizione, data_obiettivo: action.data_obiettivo || null }
        : { nome: "", descrizione: "", criterio: 80, stato: "introdotto", data_introduzione: todayISO(), data_acquisizione: null, data_obiettivo: null }
    );
  }, [open, action]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return (await api.put(`/actions/${action.action_id}`, { nome: form.nome, descrizione: form.descrizione, criterio: Number(form.criterio), data_introduzione: form.data_introduzione, data_obiettivo: form.data_obiettivo || "" })).data;
      }
      return (await api.post(`/patients/${patientId}/actions`, { ...form, domain, criterio: Number(form.criterio) })).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["actions", patientId] });
      qc.invalidateQueries({ queryKey: ["summary", patientId] });
      qc.invalidateQueries({ queryKey: ["patients"] });
      qc.invalidateQueries({ queryKey: ["reminders"] });
      qc.invalidateQueries({ queryKey: ["report", patientId] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["action", action.action_id] });
      toast.success(isEdit ? "Azione aggiornata" : `Azione “${data.nome}” creata`);
      onOpenChange(false);
      onSaved?.(data);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card" data-testid={ACTIONS.dialog}>
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Modifica azione" : `Nuova azione — ${domainLabel(domain)}`}</DialogTitle>
          <DialogDescription>Ogni azione ha il proprio grafico indipendente: crea un'azione per ogni target (es. “Batti le mani”, “Tocca la testa”).</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.nome.trim()) return toast.error("Il nome dell'azione è obbligatorio");
            mutation.mutate();
          }}
        >
          <div className="space-y-2">
            <Label>Nome azione *</Label>
            <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="es. Batti le mani" className="bg-card" data-testid={ACTIONS.nomeInput} autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Descrizione dell'azione richiesta</Label>
            <Textarea value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} placeholder="Descrivi l’azione richiesta (es. “Il bambino batte le mani su richiesta verbale ‘Batti le mani’ entro 3 secondi, senza aiuto”)." className="bg-card min-h-[90px]" data-testid={ACTIONS.descrizioneInput} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Criterio di padronanza (%)</Label>
              <Input type="number" min={1} max={100} value={form.criterio} onChange={(e) => setForm((f) => ({ ...f, criterio: e.target.value }))} className="bg-card" data-testid={ACTIONS.criterioInput} />
            </div>
            <div className="space-y-2">
              <Label>Data di introduzione</Label>
              <DatePicker value={form.data_introduzione} onChange={(v) => setForm((f) => ({ ...f, data_introduzione: v }))} testId="action-intro-date-button" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Data obiettivo (opzionale)</Label>
            <div className="flex gap-2">
              <DatePicker value={form.data_obiettivo} onChange={(v) => setForm((f) => ({ ...f, data_obiettivo: v }))} testId="action-target-date-button" placeholder="Entro quando si prevede l'acquisizione" />
              {form.data_obiettivo && (
                <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => setForm((f) => ({ ...f, data_obiettivo: null }))} aria-label="Rimuovi data obiettivo" data-testid="action-target-date-clear"><X className="h-4 w-4" /></Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Le azioni non acquisite oltre questa data vengono segnalate come in ritardo.</p>
          </div>
          {!isEdit && (
            <div className="space-y-2">
              <Label>Stato iniziale</Label>
              <RadioGroup value={form.stato} onValueChange={(v) => setForm((f) => ({ ...f, stato: v, data_acquisizione: v === "acquisito" ? f.data_acquisizione || todayISO() : null }))} className="grid grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${form.stato === "introdotto" ? "border-primary bg-accent" : "border-border"}`}>
                  <RadioGroupItem value="introdotto" data-testid="action-stato-introdotto" /> <span className="text-sm font-medium">Introdotto</span>
                </label>
                <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${form.stato === "acquisito" ? "border-emerald-600 bg-emerald-50" : "border-border"}`}>
                  <RadioGroupItem value="acquisito" data-testid="action-stato-acquisito" /> <span className="text-sm font-medium">Acquisito</span>
                </label>
              </RadioGroup>
              {form.stato === "acquisito" && (
                <div className="space-y-2 pt-1">
                  <Label>Data di acquisizione</Label>
                  <DatePicker value={form.data_acquisizione} onChange={(v) => setForm((f) => ({ ...f, data_acquisizione: v }))} testId="action-acq-date-button" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={mutation.isPending} data-testid={ACTIONS.saveButton}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? "Salva modifiche" : "Crea azione"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
